<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {watchDebounced} from '@vueuse/core';
import {
  BEAT_TYPES,
  COMMON_TIME_SIGNATURES,
  getTimeSignatureDisplayName,
} from '@/composables/musicXmlDom';
import {injectScoreEditContext} from '@/components/musicxml-preview/editContext.ts';
import Collapsible from '@/components/generic/Collapsible.vue';

// Selektions-State + Edit-Aktion aus dem von useOsmdInteractiveScore bereitgestellten Kontext
// (analog KeySignatureEditView) – kein Prop-Drilling/Emit mehr.
const {
  currentTimeSignature,
  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  saveTimeSignature,
  checkTimeSignatureOverfull,
} = injectScoreEditContext();


const selectedBeats = ref(4);
const selectedBeatType = ref(4);

const currentDisplayName = computed(() => getTimeSignatureDisplayName(selectedBeats.value, selectedBeatType.value));

// Reine Notations-Korrektur: passt das gewählte Metrum NICHT zum vorhandenen Takt-Inhalt (eine Stimme
// wäre länger als erlaubt), würde der Export eine MeterException werfen. Dann warnen + Save unterdrücken.
const isOverfull = computed(() =>
    measureNumber.value !== null
    && checkTimeSignatureOverfull(
        measureNumber.value, selectedBeats.value, selectedBeatType.value, measureIndex.value ?? undefined,
    ),
);

// Vorhandene Taktart übernehmen, sobald sich Takt/Taktart ändert.
watch(() => currentTimeSignature.value, (timeSignature) => {
  if (timeSignature) {
    selectedBeats.value = timeSignature.beats;
    selectedBeatType.value = timeSignature.beatType;
  } else {
    selectedBeats.value = 4;
    selectedBeatType.value = 4;
  }
}, {immediate: true});

// Auto-Save (analog KeySignatureEditView), aber NUR wenn das Metrum zum Inhalt passt.
watchDebounced([selectedBeats, selectedBeatType], handleSave, {debounce: 300});

function handleSave() {
  if (measureNumber.value === null || isOverfull.value) return;
  saveTimeSignature({
    measureNumber: measureNumber.value,
    measureIndex: measureIndex.value ?? undefined,
    beats: selectedBeats.value,
    beatType: selectedBeatType.value,
  });
}

function selectQuick(ts: { beats: number; beatType: number }) {
  selectedBeats.value = ts.beats;
  selectedBeatType.value = ts.beatType;
}

function isActive(ts: { beats: number; beatType: number }): boolean {
  return selectedBeats.value === ts.beats && selectedBeatType.value === ts.beatType;
}
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="measureNumber !== null"
        class="flex items-center justify-center p-2 overflow-auto"
    >
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

        <!-- Header -->
        <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-cyan-50">
          <div>
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-3">
              <div class="p-2 bg-sky-100 text-sky-600 rounded-lg">
                <Icon icon="fa6-solid:clock" class="text-lg"/>
              </div>
              Taktart bearbeiten
            </h2>
            <p class="text-sm text-gray-600 mt-1">Ab Takt {{ measureNumber }}</p>
          </div>
        </div>

        <!-- Content -->
        <div class="p-5 space-y-5">
          <div class="flex gap-2 mb-2">
            <!-- Preview -->
            <div class="flex shrink-0 grow-1 flex-col items-center justify-center text-center p-2 bg-gradient-to-r from-sky-100 to-cyan-100 rounded-xl">
              <div class="flex grow text-2xl font-bold text-sky-800 font-mono leading-none">{{ currentDisplayName }}</div>
            </div>

            <!-- Quick Select -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Schnellauswahl</label>
              <div class="flex flex-wrap gap-2">
                <button
                    v-for="ts in COMMON_TIME_SIGNATURES"
                    :key="`${ts.beats}-${ts.beatType}`"
                    @click="selectQuick(ts)"
                    class="px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors font-mono"
                    :class="isActive(ts)
                      ? 'bg-sky-100 border-sky-300 text-sky-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
                >
                  {{ ts.beats }}/{{ ts.beatType }}
                </button>
              </div>
            </div>
          </div>

          <!-- Warnung: Metrum passt nicht zum Takt-Inhalt (nicht gespeichert). -->
          <div
              v-if="isOverfull"
              class="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
          >
            <Icon icon="fa6-solid:triangle-exclamation" class="mt-0.5 shrink-0"/>
            <span>
              Das gewählte Metrum ist zu klein für den vorhandenen Takt-Inhalt – die Änderung wurde
              <strong>nicht gespeichert</strong>. Wähle eine passende Taktart (die Noten werden nicht neu aufgeteilt).
            </span>
          </div>

          <!-- Detail-Editor (eingeklappt; Preview + Schnellauswahl decken den Normalfall ab) -->
          <Collapsible variant="default" class="text-sm text-blue-500" :is-open="false">
            <template #header>
              <h5 class="text-lg">Eigene Taktart</h5>
            </template>
            <template #default>
              <!-- Zähler -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Zähler
                  <span class="text-gray-400 font-normal ml-2">({{ selectedBeats }})</span>
                </label>
                <input
                    type="range"
                    min="1"
                    max="16"
                    step="1"
                    v-model.number="selectedBeats"
                    class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
                />
              </div>

              <!-- Nenner -->
              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Nenner (Notenwert)</label>
                <div class="grid grid-cols-5 gap-1">
                  <button
                      v-for="bt in BEAT_TYPES"
                      :key="bt"
                      @click="selectedBeatType = bt"
                      class="py-2 text-sm font-medium rounded border transition-colors text-center font-mono"
                      :class="selectedBeatType === bt
                        ? 'bg-sky-500 border-sky-500 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
                  >
                    {{ bt }}
                  </button>
                </div>
              </div>
            </template>
          </Collapsible>
        </div>
      </div>
    </div>
  </Transition>
</template>
